import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Hash, MessageSquare, Lock, Search, LogOut, X } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useSidebarPanelStore, type SidebarPanel } from '@/store/sidebarPanelStore';
import { useMobile } from '@/pages/chat/ChatPage';
import StoryTray from '@/components/stories/StoryTray';
import NotificationPanel from './NotificationPanel';
import GlobalSearchModal from './GlobalSearchModal';

/**
 * Phase 21.3 (redesign) — фикс-верхняя панель: вкладки, истории, лупа
 * (toggles inline search input), нотификации, logout.
 */
export default function TopBar() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const panel = useSidebarPanelStore((s) => s.panel);
  const setPanel = useSidebarPanelStore((s) => s.setPanel);
  const { setShowSidebar } = useMobile();

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus input when search opens
  useEffect(() => {
    if (searchOpen) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [searchOpen]);

  // Open modal when user types ≥ 2 chars (debounced 250ms)
  useEffect(() => {
    if (!searchOpen) return;
    const t = setTimeout(() => {
      if (searchQuery.trim().length >= 2) setModalOpen(true);
    }, 250);
    return () => clearTimeout(t);
  }, [searchQuery, searchOpen]);

  // Cmd/Ctrl+K — focus inline search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setSearchQuery('');
        setModalOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/auth/login');
  };

  const go = (p: SidebarPanel) => {
    setPanel(p);
    setShowSidebar(true);
    if (p === 'dm') navigate('/dm');
    else navigate('/');
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setModalOpen(false);
  };

  const Tab = ({
    value,
    icon: Icon,
    label,
    badge,
    hideOnMobile,
  }: {
    value: SidebarPanel;
    icon: typeof Hash;
    label: string;
    badge?: number;
    hideOnMobile?: boolean;
  }) => (
    <button
      onClick={() => go(value)}
      className={clsx(
        'flex items-center gap-1.5 px-3 h-full text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
        panel === value
          ? 'text-brand-300 border-brand-400'
          : 'text-surface-400 border-transparent hover:text-surface-200',
        hideOnMobile && 'hidden md:inline-flex',
      )}
    >
      <Icon size={14} />
      <span>{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="ml-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-[10px] leading-[16px] text-center font-semibold">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );

  return (
    <header className="h-[72px] flex-shrink-0 bg-surface-900 border-b border-surface-800 flex items-stretch px-2 gap-1 md:gap-2 sticky top-0 z-20">
      {/* Tabs */}
      <nav className="flex items-stretch">
        <Tab value="channels" icon={Hash} label="Каналы" />
        <Tab value="dm" icon={MessageSquare} label="Диалоги" />
        <Tab value="secret" icon={Lock} label="Секрет" hideOnMobile />
      </nav>

      {/* Middle area — stories tray OR search input */}
      <div className="flex-1 min-w-0 self-center relative">
        {searchOpen ? (
          <div className="flex items-center gap-2 bg-surface-800 border border-surface-700 rounded-lg px-3 h-10 focus-within:border-brand-500 transition-colors">
            <Search size={14} className="text-surface-400 flex-shrink-0" />
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.trim().length >= 2) setModalOpen(true);
              }}
              placeholder="Поиск сообщений, каналов, людей…"
              className="bg-transparent text-sm text-surface-100 outline-none w-full placeholder:text-surface-500"
            />
            <button
              type="button"
              onClick={closeSearch}
              title="Закрыть"
              className="text-surface-500 hover:text-surface-200 flex-shrink-0"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto scrollbar-none">
            <StoryTray />
          </div>
        )}
      </div>

      {/* Right cluster */}
      <div className="flex items-center gap-1.5 pl-2">
        {!searchOpen && (
          <button
            onClick={() => setSearchOpen(true)}
            title="Глобальный поиск (Ctrl+K)"
            className="w-9 h-9 rounded-full bg-surface-800 text-surface-400 hover:bg-surface-700 hover:text-surface-200 flex items-center justify-center transition-colors"
          >
            <Search size={15} />
          </button>
        )}

        <NotificationPanel />

        <button
          onClick={handleLogout}
          title={user ? `Выйти (${user.username})` : 'Выйти'}
          className="w-9 h-9 rounded-full bg-surface-800 text-surface-400 hover:bg-red-700 hover:text-white flex items-center justify-center transition-colors"
        >
          <LogOut size={15} />
        </button>
      </div>

      {modalOpen && (
        <GlobalSearchModal
          onClose={closeSearch}
          initialQuery={searchQuery}
        />
      )}
    </header>
  );
}
